import type {
  AccessBulkAction,
  AccessDeactivationQueueItem,
  AccessPasswordHandling,
  AccessQueueItemPublic,
  AccessQueueType,
  AccessUserRef,
  SessionUser,
} from '@accounts/shared';
import { ACCESS_QUEUE_TYPES } from '@accounts/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type CSSProperties, Fragment, type ReactNode, useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { STR } from '../../../i18n';
import {
  ApiError,
  approveAccessRequest,
  bulkAccessAction,
  denyAccessRequest,
  listAccessLevels,
  listAccessNodes,
} from '../../../lib/api';
import { relativeTime } from '../../../lib/format';
import { queryKeys } from '../../../lib/queryKeys';
import { showToast } from '../../../lib/toast';
import { Callout, Card, Chip, EmptyRow, Skeleton } from '../../../ui/Primitives';
import {
  checkboxStyle,
  monoStyle,
  quietButtonStyle,
  rowActionStyle,
  searchFieldStyle,
} from '../../../ui/styles';

import { itemLabel, passwordHandlingLabel } from './accessFormat';
import { type ItFlowStart, ItEmailFlow } from './itEmail/ItEmailFlow';
import { isWithIt, pickable } from './itEmail/picker';
import { useItEmails, useItStanding } from './itEmail/useItEmails';
import { WAITING_ON_IT_LANE } from './itEmail/waiting';
import { WaitingOnItLane } from './itEmail/WaitingOnItLane';
import { OtherRequestsLane } from './other/OtherRequestsLane';
import { ApproveDialog, DenyDialog } from './queue/QueueDialogs';
import { isLimitedManager, itemKey, useRoleQueue } from './queue/useRoleQueue';

/**
 * THE QUEUE (FINAL demo R3-2, spec row 3): every pending request and every outside-system
 * record waiting on someone, oldest first, in one lane per type. Filters, search and the
 * overdue strip all work on the ONE role-scoped read (`useRoleQueue`), so the counts at the
 * top, the lanes and the hub's sub-nav are the same rows.
 *
 * `?type=` and `?user=` are the contract other screens link with (the Overview's tiles, the
 * person page). `?type=overdue` is the Overview's Overdue tile: every lane, overdue only.
 * `?type=it` is its Waiting on IT tile: the Waiting on IT lane alone, without the overdue
 * strip, the bulk bar or the row filters, which all work on the other lanes' rows.
 *
 * Spec rows 13 and 15 join through their own reads, so no Queue response changes: Other
 * requests (administrators only) get their own lane, and records in a SENT IT email leave
 * their lanes for Waiting on IT (one block per email). A record in a draft stays in its lane,
 * marked so. "Draft email to IT" sits on the Needs access and Remove access lanes and in the
 * bulk bar. `?requestId=` / `?otherRequestId=` (the bell's links) mark the row they name.
 *
 * App deactivations are read-only: Accounts shows their pending / overdue state here, while
 * only the consuming application can confirm one.
 */

type AgeFilter = 'all' | 'overdue' | 'ontime';
type QueueTypeFilter = AccessQueueType | 'deactivations' | typeof WAITING_ON_IT_LANE;
type MarkAction = Extract<AccessBulkAction, 'mark_granted' | 'mark_removed'>;

/** The server takes at most 200 ids per bulk call. */
const BULK_LIMIT = 200;

const isQueueType = (value: string | null): value is AccessQueueType =>
  value !== null && (ACCESS_QUEUE_TYPES as readonly string[]).includes(value);

const isQueueTypeFilter = (value: string | null): value is QueueTypeFilter =>
  value === 'deactivations' || value === WAITING_ON_IT_LANE || isQueueType(value);

interface DoneLine {
  key: string;
  type: AccessQueueType;
  label: string;
  tone: 'ok' | 'bad';
  userName: string;
  item: string;
  at: string;
  note: string | null;
}

type Dialog =
  | { kind: 'approve'; items: AccessQueueItemPublic[] }
  | { kind: 'deny'; items: AccessQueueItemPublic[] }
  | null;

type Operation =
  | {
      kind: 'approve';
      items: AccessQueueItemPublic[];
      modes: Readonly<Record<string, AccessPasswordHandling>>;
    }
  | { kind: 'deny'; items: AccessQueueItemPublic[]; note: string }
  | { kind: 'mark'; action: MarkAction; items: AccessQueueItemPublic[] };

function chunks<T>(values: readonly T[]): T[][] {
  const out: T[][] = [];
  for (let at = 0; at < values.length; at += BULK_LIMIT) out.push(values.slice(at, at + BULK_LIMIT));
  return out;
}

/** The work a mark action applies to — the server refuses any other status. */
function fitsMark(item: AccessQueueItemPublic, action: MarkAction): boolean {
  return action === 'mark_granted'
    ? item.type === 'needs_access' || item.type === 'requested'
    : item.type === 'remove_access';
}

/**
 * Runs one operation. Approvals and denials are request ids, marks are record ids — the
 * bulk endpoint's own split. A single item goes through the single route where one exists,
 * so the audit row reads as a one-off decision; anything more is one bulk call per 200.
 */
async function perform(op: Operation): Promise<void> {
  const ids = (items: readonly AccessQueueItemPublic[]) => items.map((item) => item.id);

  if (op.kind === 'deny') {
    const only = op.items[0];
    if (op.items.length === 1 && only !== undefined) {
      await denyAccessRequest(only.id, op.note);
    } else {
      for (const part of chunks(ids(op.items))) {
        await bulkAccessAction({ action: 'deny', ids: part, note: op.note });
      }
    }
    return;
  }

  if (op.kind === 'mark') {
    // Even a single mark goes through the bulk route: it is the one path that writes the
    // `access_requests.mark_granted` / `mark_removed` verbs, so one tick and a hundred read
    // the same in the audit log.
    for (const part of chunks(ids(op.items))) {
      await bulkAccessAction({ action: op.action, ids: part });
    }
    return;
  }

  /*
   * PASSWORD HANDLING RIDES ON THE APPROVAL (spec row 16). Each outside row's pick goes in
   * the approve call itself, so the new record and the approval audit row carry it in one
   * write. Connected-app rows name none: the server refuses handling on them.
   */
  const modes = op.modes;
  const handlingOf = (item: AccessQueueItemPublic): AccessPasswordHandling | undefined =>
    item.catalogItem.applicationId === null ? (modes[item.id] ?? 'managed') : undefined;

  const single = op.items[0];
  if (op.items.length === 1 && single !== undefined) {
    await approveAccessRequest(single.id, handlingOf(single));
    return;
  }
  for (const part of chunks(op.items)) {
    const passwordHandlingById: Record<string, AccessPasswordHandling> = {};
    for (const item of part) {
      const handling = handlingOf(item);
      if (handling !== undefined) passwordHandlingById[item.id] = handling;
    }
    await bulkAccessAction({ action: 'approve', ids: ids(part), passwordHandlingById });
  }
}

const laneRowGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '24px 96px minmax(120px,.9fr) minmax(140px,1fr) minmax(160px,1.3fr) auto',
  gap: 10,
  alignItems: 'center',
  padding: '7px 16px',
  borderBottom: '1px solid var(--border)',
  fontSize: 12.5,
};

const ellipsis: CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const overdueBar: CSSProperties = { boxShadow: 'inset 3px 0 0 var(--bg-danger)' };

const selectStyle: CSSProperties = { ...searchFieldStyle, width: 'auto', fontSize: 12 };

/** One login several people use, waiting in the same lane. */
interface SharedGroup {
  key: string;
  members: AccessQueueItemPublic[];
  holders: AccessUserRef[];
}

type LaneEntry = { item: AccessQueueItemPublic } | { group: SharedGroup };

/**
 * SHARED LOGINS ACT AS ONE LINE. Records on the same catalog row with the same login
 * (case-insensitive — the server's own rule for `sharedWith`) that wait in the same lane
 * fold into one group row, which names everyone who uses the login. A shared login with
 * only one holder waiting stays a plain row that says who else uses it.
 */
function laneEntries(items: readonly AccessQueueItemPublic[]): LaneEntry[] {
  const groups = new Map<string, AccessQueueItemPublic[]>();
  for (const item of items) {
    if (item.type === 'request' || item.login === null || item.sharedWith.length === 0) continue;
    const key = `${item.catalogItem.id}|${item.login.toLowerCase()}`;
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }

  const emitted = new Set<string>();
  const out: LaneEntry[] = [];
  for (const item of items) {
    const key =
      item.type === 'request' || item.login === null
        ? null
        : `${item.catalogItem.id}|${item.login.toLowerCase()}`;
    const members = key === null ? undefined : groups.get(key);
    if (key === null || members === undefined || members.length < 2) {
      out.push({ item });
      continue;
    }
    if (emitted.has(key)) continue;
    emitted.add(key);
    const holders = new Map<string, AccessUserRef>();
    for (const member of members) {
      holders.set(member.user.id, member.user);
      for (const other of member.sharedWith) holders.set(other.id, other);
    }
    out.push({ group: { key, members, holders: [...holders.values()] } });
  }
  return out;
}

export function QueuePanel({ user }: { user: SessionUser }) {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const queue = useRoleQueue(user);
  const hrOnly = isLimitedManager(user);

  const typeParam = params.get('type');
  const typeFilter: QueueTypeFilter | null = isQueueTypeFilter(typeParam) ? typeParam : null;
  const itOnly = typeFilter === WAITING_ON_IT_LANE;
  const userFilter = params.get('user');

  const [search, setSearch] = useState('');
  const [ageChoice, setAgeChoice] = useState<AgeFilter>('all');
  const age: AgeFilter = typeParam === 'overdue' ? 'overdue' : ageChoice;
  const [levelPicks, setLevelPicks] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(new Set());
  const [closedLanes, setClosedLanes] = useState<ReadonlySet<AccessQueueType>>(new Set());
  const [done, setDone] = useState<DoneLine[]>([]);
  const [armed, setArmed] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [itFlow, setItFlow] = useState<ItFlowStart | null>(null);
  const it = useItStanding();
  const itEmails = useItEmails();
  const focusRequest = params.get('requestId');
  const focusOther = params.get('otherRequestId');

  const levels = useQuery({ queryKey: queryKeys.accessLevels, queryFn: () => listAccessLevels() });
  const nodes = useQuery({
    queryKey: queryKeys.accessNodes(false),
    queryFn: () => listAccessNodes(),
  });

  // Stable, so an open dialog's Escape handler is not re-bound (and focus not re-taken) every
  // time the Queue re-renders under it on a background refetch.
  const closeDialog = useCallback(() => {
    setDialog(null);
    setDialogError(null);
  }, []);

  function setParam(name: 'type' | 'user', value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(name);
    else next.set(name, value);
    setParams(next, { replace: true });
  }

  const mutation = useMutation({
    mutationFn: perform,
    onSuccess: (_result, op) => {
      const at = new Date().toISOString();
      const lines: DoneLine[] = op.items.map((item) => ({
        key: itemKey(item),
        type: item.type,
        label:
          op.kind === 'deny'
            ? STR.access.queue.doneDenied
            : op.kind === 'mark'
              ? op.action === 'mark_granted'
                ? STR.access.queue.doneGranted
                : STR.access.queue.doneRemoved
              : item.catalogItem.applicationId === null
                ? STR.access.queue.doneApprovedNeeds(
                    passwordHandlingLabel(op.modes[item.id] ?? 'managed'),
                  )
                : STR.access.queue.doneApprovedGranted,
        tone: op.kind === 'deny' ? 'bad' : 'ok',
        userName: item.user.name,
        item: itemLabel(item.path, item.catalogItem.name),
        at,
        note: op.kind === 'deny' ? op.note : null,
      }));
      setDone((current) => [...lines, ...current]);
      setSelected((current) => {
        const next = new Set(current);
        for (const line of lines) next.delete(line.key);
        return next;
      });
      closeDialog();
      const n = op.items.length;
      showToast(
        op.kind === 'deny'
          ? STR.access.queue.toastDenied(n)
          : op.kind === 'mark'
            ? op.action === 'mark_granted'
              ? STR.access.queue.toastGranted(n)
              : STR.access.queue.toastRemoved(n)
            : STR.access.queue.toastApproved(n),
      );
    },
    onError: (error) => {
      const message = error instanceof ApiError ? error.message : STR.errors.generic;
      if (dialog === null) showToast(message, 'warn');
      else setDialogError(message);
    },
    // Settled, not success: a bulk run that failed part-way may still have changed rows (each
    // 200-id call is its own transaction), so every view re-reads either way.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.allGrants });
      // The red count rides the bell's read.
      void queryClient.invalidateQueries({ queryKey: queryKeys.myNotifications });
    },
  });

  /** Approve: at once when every item is a suite app; otherwise ask the password mode. */
  function startApprove(items: AccessQueueItemPublic[]) {
    const requests = items.filter((item) => item.type === 'request');
    if (requests.length === 0) return;
    if (requests.every((item) => item.catalogItem.applicationId !== null)) {
      mutation.mutate({ kind: 'approve', items: requests, modes: {} });
      return;
    }
    setDialogError(null);
    setDialog({ kind: 'approve', items: requests });
  }

  function startDeny(items: AccessQueueItemPublic[]) {
    const requests = items.filter((item) => item.type === 'request');
    if (requests.length === 0) return;
    setDialogError(null);
    setDialog({ kind: 'deny', items: requests });
  }

  function mark(action: MarkAction, items: AccessQueueItemPublic[]) {
    const fit = items.filter((item) => fitsMark(item, action));
    if (fit.length > 0) mutation.mutate({ kind: 'mark', action, items: fit });
  }

  if (queue.isLoading) {
    return (
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Skeleton style={{ height: 56, borderRadius: 12 }} />
        <Skeleton style={{ height: 32, width: 520, borderRadius: 8 }} />
        {[1, 2, 3].map((n) => (
          <Skeleton key={n} style={{ height: 140, borderRadius: 12 }} />
        ))}
      </div>
    );
  }

  if (queue.isError) {
    return (
      <div style={{ padding: '20px 24px' }}>
        <Callout
          tone="danger"
          role="alert"
          style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
        >
          <span style={{ flex: 1, minWidth: 180 }}>{STR.access.queue.loadFailed}</span>
          <button type="button" className="tbtn" style={quietButtonStyle} onClick={queue.refetch}>
            {STR.common.tryAgain}
          </button>
        </Callout>
      </div>
    );
  }

  // A record in a sent IT email is drawn under Waiting on IT, not in its lane.
  const all = queue.items.filter((item) => !isWithIt(item, it.standing));
  const forIt = pickable(all, it.standing);
  const allDeactivations = hrOnly ? [] : queue.deactivations;
  const needle = search.trim().toLowerCase();
  const pickedNodes = Object.values(levelPicks).filter((id) => id !== '');
  // Everything except the type: the lanes ARE the type split, and the strip counts across them.
  const items = all.filter(
    (item) =>
      (userFilter === null || item.user.id === userFilter) &&
      (age === 'all' || (age === 'overdue' ? item.overdue : !item.overdue)) &&
      // A node filter matches the node and everything below it: the item's path holds it.
      pickedNodes.every((nodeId) => item.path.some((step) => step.id === nodeId)) &&
      (needle === '' ||
        `${item.user.name}\n${item.user.email}\n${item.login ?? ''}`
          .toLowerCase()
          .includes(needle)),
  );
  const lanesShown: readonly AccessQueueType[] =
    typeFilter === null
      ? ACCESS_QUEUE_TYPES
      : typeFilter === 'deactivations' || itOnly
        ? []
        : [typeFilter];
  const shown = items.filter((item) => lanesShown.includes(item.type));
  const deactivations = allDeactivations.filter(
    (item) =>
      (typeFilter === null || typeFilter === 'deactivations') &&
      (userFilter === null || item.user.id === userFilter) &&
      (age === 'all' || (age === 'overdue' ? item.overdue : !item.overdue)) &&
      pickedNodes.length === 0 &&
      (needle === '' ||
        `${item.user.name}\n${item.user.email}\n${item.application.name}\n${item.application.key}`
          .toLowerCase()
          .includes(needle)),
  );
  const overdue = shown.filter((item) => item.overdue);
  const overdueDeactivations = deactivations.filter((item) => item.overdue);
  const oldestOverdue = overdue[0];
  const oldestDeactivation = overdueDeactivations[0];
  const oldestIsDeactivation =
    oldestDeactivation !== undefined &&
    (oldestOverdue === undefined || oldestDeactivation.createdAt < oldestOverdue.since);
  const selectedItems = all.filter((item) => selected.has(itemKey(item)));
  const nRequests = selectedItems.filter((item) => item.type === 'request').length;
  const nGranted = selectedItems.filter((item) => fitsMark(item, 'mark_granted')).length;
  const nRemoved = selectedItems.filter((item) => fitsMark(item, 'mark_removed')).length;
  const selectedForIt = forIt.filter((item) => selected.has(itemKey(item)));
  const busy = mutation.isPending;

  const people = [
    ...new Map(
      [...all.map((item) => item.user), ...allDeactivations.map((item) => item.user)].map(
        (person) => [person.id, person] as const,
      ),
    ).values(),
  ].sort((a, b) => a.name.localeCompare(b.name));
  const filtersActive =
    typeParam !== null ||
    userFilter !== null ||
    ageChoice !== 'all' ||
    pickedNodes.length > 0 ||
    needle !== '';

  function toggle(keys: readonly string[], on: boolean) {
    setSelected((current) => {
      const next = new Set(current);
      for (const key of keys) {
        if (on) next.add(key);
        else next.delete(key);
      }
      return next;
    });
  }

  function selectBy(kind: AccessQueueType | 'overdue') {
    toggle(
      shown
        .filter((item) => (kind === 'overdue' ? item.overdue : item.type === kind))
        .map(itemKey),
      true,
    );
  }

  function ageCell(item: AccessQueueItemPublic): ReactNode {
    return (
      <span
        className={item.overdue ? 'tx-danger-strong' : 'tx-subtle'}
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontSize: 11.5,
          fontWeight: item.overdue ? 700 : 400,
        }}
      >
        {item.overdue ? STR.access.queue.overdueAge(relativeTime(item.since)) : relativeTime(item.since)}
      </span>
    );
  }

  function rowActions(item: AccessQueueItemPublic): ReactNode {
    if (item.type === 'request') {
      return (
        <>
          <button
            type="button"
            className="tbtn"
            style={rowActionStyle}
            disabled={busy}
            onClick={() => {
              startApprove([item]);
            }}
          >
            {STR.access.queue.approve}
          </button>
          <button
            type="button"
            className="tbtn tbtn-danger"
            style={rowActionStyle}
            disabled={busy}
            onClick={() => {
              startDeny([item]);
            }}
          >
            {STR.access.queue.deny}
          </button>
        </>
      );
    }
    const action: MarkAction = item.type === 'remove_access' ? 'mark_removed' : 'mark_granted';
    return (
      <button
        type="button"
        className="tbtn"
        style={rowActionStyle}
        disabled={busy}
        onClick={() => {
          mark(action, [item]);
        }}
      >
        {action === 'mark_removed' ? STR.access.queue.markRemoved : STR.access.queue.markGranted}
      </button>
    );
  }

  function rowDetail(item: AccessQueueItemPublic): ReactNode {
    if (item.type === 'request') {
      // FINAL `rowDetail`: "Onboarding, " on a request onboarding filed, then the requester's
      // note or "No note". A row with no source is not request-backed and keeps the old hint.
      if (item.source === null) {
        return (
          <span className="tx-subtle">
            {item.catalogItem.applicationId === null ? STR.access.queue.outsideSystem : STR.access.queue.suiteApp}
          </span>
        );
      }
      const onboarding = item.source === 'onboarding';
      return (
        <span style={{ ...ellipsis, display: 'block' }} title={item.note ?? undefined}>
          {onboarding ? STR.access.queue.onboardingPrefix : null}
          {item.note === null ? (
            <span className="tx-subtle">
              {onboarding ? STR.access.queue.noNoteAfterOnboarding : STR.access.queue.noNote}
            </span>
          ) : (
            STR.access.queue.noteQuoted(item.note)
          )}
        </span>
      );
    }
    return (
      <span style={{ ...ellipsis, display: 'block' }}>
        {item.login === null ? (
          <span className="tx-subtle">{STR.accessCommon.noLogin}</span>
        ) : (
          <span style={{ ...monoStyle, fontSize: 12 }}>{item.login}</span>
        )}
        {it.standing.draft.has(item.id) ? (
          <span className="tx-subtle">{STR.itEmails.inDraft}</span>
        ) : null}
        {item.sharedWith.length > 0 ? (
          <span className="tx-subtle" style={{ display: 'block', fontSize: 11.5 }}>
            {STR.access.queue.sharedWith(item.sharedWith.map((other) => other.name).join(', '))}
          </span>
        ) : null}
      </span>
    );
  }

  function line(item: AccessQueueItemPublic, nested = false): ReactNode {
    const key = itemKey(item);
    return (
      <li
        key={key}
        className="row"
        style={{
          ...laneRowGrid,
          ...(nested ? { paddingLeft: 32 } : {}),
          ...(item.overdue ? overdueBar : {}),
          ...(item.type === 'request' && item.id === focusRequest
            ? { background: 'var(--surface-2)' }
            : {}),
        }}
      >
        <input
          type="checkbox"
          style={checkboxStyle}
          checked={selected.has(key)}
          onChange={(event) => {
            toggle([key], event.target.checked);
          }}
          aria-label={STR.access.queue.selectRow(item.user.name)}
        />
        {ageCell(item)}
        <span style={ellipsis}>
          <span style={{ fontWeight: 650 }}>{item.user.name}</span>
          <span className="tx-subtle" style={{ display: 'block', fontSize: 11.5, ...ellipsis }}>
            {item.user.email}
          </span>
        </span>
        <span style={ellipsis}>{itemLabel(item.path, item.catalogItem.name)}</span>
        <span style={{ minWidth: 0, fontSize: 12 }}>{rowDetail(item)}</span>
        <span style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          {rowActions(item)}
        </span>
      </li>
    );
  }

  function groupLine(type: AccessQueueType, group: SharedGroup): ReactNode {
    const keys = group.members.map(itemKey);
    const first = group.members[0];
    if (first === undefined) return null;
    const waiting = new Set(group.members.map((member) => member.user.id));
    const open = openGroups.has(group.key);
    const action: MarkAction = type === 'remove_access' ? 'mark_removed' : 'mark_granted';
    return (
      <li key={group.key} style={{ listStyle: 'none' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '24px 96px minmax(0,1fr) auto',
            gap: 10,
            alignItems: 'center',
            padding: '9px 16px',
            borderBottom: '1px solid var(--border)',
            background: 'var(--surface-2)',
            fontSize: 12.5,
            ...(group.members.some((member) => member.overdue) ? overdueBar : {}),
          }}
        >
          <input
            type="checkbox"
            style={checkboxStyle}
            checked={keys.every((key) => selected.has(key))}
            onChange={(event) => {
              toggle(keys, event.target.checked);
            }}
            aria-label={STR.access.queue.selectGroup}
          />
          {ageCell(first)}
          <div style={{ minWidth: 0 }}>
            <div>
              <b style={{ fontWeight: 650 }}>
                {STR.access.queue.sharedGroup(itemLabel(first.path, first.catalogItem.name))}
              </b>{' '}
              <span style={{ ...monoStyle, fontSize: 12 }}>{first.login}</span>
              <span className="tx-muted">
                {STR.access.queue.sharedGroupWaiting(group.members.length, group.holders.length)}
              </span>
            </div>
            <div className="tx-subtle" style={{ fontSize: 11.5, marginTop: 2 }}>
              {group.holders.map((holder, index) => (
                <span key={holder.id}>
                  {index > 0 ? ', ' : null}
                  {waiting.has(holder.id) ? (
                    <b className="tx-muted" style={{ fontWeight: 650 }}>
                      {holder.name}
                    </b>
                  ) : (
                    holder.name
                  )}
                </span>
              ))}
            </div>
          </div>
          <span style={{ display: 'flex', gap: 2 }}>
            <button
              type="button"
              className="tbtn"
              style={rowActionStyle}
              disabled={busy}
              onClick={() => {
                mark(action, group.members);
              }}
            >
              {action === 'mark_removed'
                ? STR.access.queue.markAllRemoved(group.members.length)
                : STR.access.queue.markAllGranted(group.members.length)}
            </button>
            <button
              type="button"
              className="tbtn"
              style={rowActionStyle}
              aria-expanded={open}
              onClick={() => {
                setOpenGroups((current) => {
                  const next = new Set(current);
                  if (open) next.delete(group.key);
                  else next.add(group.key);
                  return next;
                });
              }}
            >
              {open ? STR.access.queue.collapse : STR.access.queue.oneByOne}
            </button>
          </span>
        </div>
        {open ? (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {group.members.map((member) => line(member, true))}
          </ul>
        ) : null}
      </li>
    );
  }

  function lane(type: AccessQueueType): ReactNode {
    const laneItems = items.filter((item) => item.type === type);
    const laneDone = done.filter((entry) => entry.type === type);
    const laneOverdue = laneItems.filter((item) => item.overdue).length;
    const closed = closedLanes.has(type);
    const laneKey = `lane:${type}`;

    let doAll: ReactNode = null;
    if (laneItems.length > 0 && type === 'request') {
      // Two clicks: "Approve all" is the one control here that decides many people at once.
      const isArmed = armed === laneKey;
      doAll = (
        <button
          type="button"
          className="prim"
          style={{ ...rowActionStyle, fontWeight: 650 }}
          disabled={busy}
          onClick={() => {
            if (!isArmed) {
              setArmed(laneKey);
              showToast(STR.access.queue.approveAllConfirm, 'warn');
              return;
            }
            setArmed(null);
            startApprove(laneItems);
          }}
        >
          {isArmed
            ? STR.access.queue.approveAllArmed(laneItems.length)
            : STR.access.queue.approveAll(laneItems.length)}
        </button>
      );
    } else if (laneItems.length > 0) {
      const action: MarkAction = type === 'remove_access' ? 'mark_removed' : 'mark_granted';
      const laneForIt = forIt.filter((item) => item.type === type && laneItems.includes(item));
      const draftForLane =
        laneForIt.length > 0 ? (
          <button
            type="button"
            className="ghost"
            style={rowActionStyle}
            disabled={busy}
            onClick={() => {
              setItFlow({ kind: 'pick', initial: new Set(laneForIt.map((item) => item.id)) });
            }}
          >
            {STR.itEmails.draftForLane(laneForIt.length)}
          </button>
        ) : null;
      doAll = (
        <>
          {draftForLane}
          <button
            type="button"
            className="tbtn"
            style={rowActionStyle}
            disabled={busy}
            onClick={() => {
              mark(action, laneItems);
            }}
          >
            {action === 'mark_removed'
              ? STR.access.queue.markAllRemoved(laneItems.length)
              : STR.access.queue.markAllGranted(laneItems.length)}
          </button>
        </>
      );
    }

    return (
      <Card key={type}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '11px 16px',
            borderBottom: closed ? 'none' : '1px solid var(--border)',
            background: 'var(--surface-2)',
            fontSize: 13.5,
            fontWeight: 650,
          }}
        >
          <button
            type="button"
            className="tbtn"
            aria-expanded={!closed}
            onClick={() => {
              setClosedLanes((current) => {
                const next = new Set(current);
                if (closed) next.delete(type);
                else next.add(type);
                return next;
              });
            }}
            style={{ ...quietButtonStyle, color: 'var(--text)', padding: '2px 6px' }}
          >
            {STR.access.queue.type[type]}{' '}
            <span className="tx-subtle" style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {laneItems.length}
            </span>
          </button>
          {laneOverdue > 0 ? (
            <span className="tx-danger-strong" style={{ fontSize: 12, fontWeight: 700 }}>
              {STR.access.queue.laneOverdue(laneOverdue)}
            </span>
          ) : null}
        </div>
        {closed ? null : (
          <>
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                padding: '8px 16px',
                borderBottom: '1px solid var(--border)',
                flexWrap: 'wrap',
              }}
            >
              <span className="tx-subtle" style={{ fontSize: 11.5 }}>
                {STR.access.queue.laneHelp[type]}
              </span>
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>{doAll}</span>
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {laneDone.map((entry) => (
                <li
                  key={`done:${entry.key}:${entry.at}`}
                  data-off
                  className="row"
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'center',
                    padding: '7px 16px 7px 50px',
                    borderBottom: '1px solid var(--border)',
                    fontSize: 12,
                    background: 'var(--surface-2)',
                  }}
                >
                  <b
                    className={entry.tone === 'bad' ? 'tx-danger-strong' : 'tx-success-strong'}
                    style={{ fontWeight: 650 }}
                  >
                    {entry.label}
                  </b>
                  <span className="tx-subtle">
                    {STR.access.queue.doneLine(entry.userName, entry.item, relativeTime(entry.at))}
                    {entry.note === null ? null : ` — “${entry.note}”`}
                  </span>
                </li>
              ))}
              {laneItems.length === 0 && laneDone.length === 0 ? (
                <li>
                  <EmptyRow>{STR.access.queue.laneEmpty}</EmptyRow>
                </li>
              ) : null}
              {laneEntries(laneItems).map((entry) =>
                'group' in entry ? groupLine(type, entry.group) : line(entry.item),
              )}
            </ul>
          </>
        )}
      </Card>
    );
  }

  // Administrators only: HR never sees an Other request (the server 403s its read too).
  const showOther = user.isAdmin && (typeFilter === null || typeFilter === 'request');

  function deactivationLane(entries: readonly AccessDeactivationQueueItem[]): ReactNode {
    if (typeFilter !== null && typeFilter !== 'deactivations') return null;
    return (
      <Card>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '11px 16px',
            borderBottom: '1px solid var(--border)',
            background: 'var(--surface-2)',
            fontSize: 13.5,
            fontWeight: 650,
          }}
        >
          <span>{STR.access.queue.deactivations.title}</span>
          <span className="tx-subtle" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {entries.length}
          </span>
          {entries.some((item) => item.overdue) ? (
            <span className="tx-danger-strong" style={{ fontSize: 12, fontWeight: 700 }}>
              {STR.access.queue.laneOverdue(entries.filter((item) => item.overdue).length)}
            </span>
          ) : null}
        </div>
        <div className="tx-subtle" style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)', fontSize: 11.5 }}>
          {STR.access.queue.deactivations.help(queue.deprovisionOverdueHours)}
        </div>
        {entries.length === 0 ? (
          <EmptyRow>{STR.access.queue.laneEmpty}</EmptyRow>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {entries.map((item) => (
              <li
                key={item.id}
                className="row"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '96px minmax(0,1fr) minmax(0,1fr) auto',
                  gap: 10,
                  alignItems: 'center',
                  padding: '10px 16px',
                  borderBottom: '1px solid var(--border)',
                  ...(item.overdue ? overdueBar : {}),
                }}
              >
                <span
                  className={item.overdue ? 'tx-danger-strong' : 'tx-subtle'}
                  style={{ fontSize: 11.5, fontWeight: item.overdue ? 700 : 400 }}
                >
                  {item.overdue
                    ? STR.access.queue.overdueAge(relativeTime(item.createdAt))
                    : relativeTime(item.createdAt)}
                </span>
                <span style={ellipsis}>
                  <b style={{ display: 'block', fontSize: 12.5 }}>{item.user.name}</b>
                  <span className="tx-subtle" style={{ display: 'block', fontSize: 11.5, ...ellipsis }}>
                    {item.user.email}
                  </span>
                </span>
                <span style={ellipsis}>
                  <b style={{ display: 'block', fontSize: 12.5 }}>{item.application.name}</b>
                  <span className="tx-subtle" style={{ display: 'block', fontSize: 11.5, ...monoStyle }}>
                    {item.application.key}
                  </span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="tx-subtle" style={{ fontSize: 11.5 }}>
                    {STR.access.queue.deactivations.doorbell(item.doorbell.attempts)}
                  </span>
                  <Chip tone={item.overdue ? 'danger' : 'attention'} size="sm">
                    {item.overdue
                      ? STR.access.queue.deactivations.overdue
                      : STR.access.queue.deactivations.pending}
                  </Chip>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    );
  }

  const levelList = [...(levels.data?.data ?? [])].sort((a, b) => a.depth - b.depth);
  const liveNodes = nodes.data?.data ?? [];

  return (
    <div style={{ padding: '20px 24px 110px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {hrOnly ? <Callout tone="neutral">{STR.access.queue.hrNote}</Callout> : null}

      {itOnly ? null : overdue.length + overdueDeactivations.length > 0 ? (
        <Callout
          tone="danger"
          style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', borderRadius: 12 }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>
              {STR.access.queue.overdueCount(overdue.length + overdueDeactivations.length)}
            </div>
            <div style={{ fontSize: 12 }}>
              {oldestIsDeactivation
                ? STR.access.queue.deactivations.oldest(
                    oldestDeactivation.user.name,
                    oldestDeactivation.application.name,
                    relativeTime(oldestDeactivation.createdAt),
                  )
                : oldestOverdue === undefined
                  ? STR.common.dash
                  : STR.access.queue.oldest(
                    oldestOverdue.user.name,
                    itemLabel(oldestOverdue.path, oldestOverdue.catalogItem.name),
                    relativeTime(oldestOverdue.since),
                    )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 12, fontWeight: 650 }}>
            {ACCESS_QUEUE_TYPES.map((type) => {
              const n = overdue.filter((item) => item.type === type).length;
              return n > 0 ? <span key={type}>{STR.access.queue.typeCount(type, n)}</span> : null;
            })}
            {overdueDeactivations.length > 0 ? (
              <span>{STR.access.queue.deactivations.overdueCount(overdueDeactivations.length)}</span>
            ) : null}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            {overdue.length > 0 ? (
              <button
                type="button"
                className="ghost"
                style={rowActionStyle}
                onClick={() => {
                  selectBy('overdue');
                }}
              >
                {STR.access.queue.selectAllOverdue}
              </button>
            ) : null}
            <button
              type="button"
              className="ghost"
              style={rowActionStyle}
              onClick={() => {
                if (age === 'overdue') {
                  setAgeChoice('all');
                  if (typeParam === 'overdue') setParam('type', null);
                } else {
                  setAgeChoice('overdue');
                }
              }}
            >
              {age === 'overdue' ? STR.access.queue.showEverything : STR.access.queue.showOnlyOverdue}
            </button>
          </div>
        </Callout>
      ) : (
        <Callout tone="positive">{STR.access.queue.nothingOverdue}</Callout>
      )}

      {/* Filters. Every one narrows the rows in hand; none issues a read of its own. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {itOnly ? null : (
          <input
            className="field"
            type="search"
            value={search}
            placeholder={STR.access.queue.searchPlaceholder}
            aria-label={STR.access.queue.searchLabel}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
            style={{ ...searchFieldStyle, width: 220 }}
          />
        )}
        <select
          className="field"
          aria-label={STR.access.queue.typeLabel}
          value={typeFilter ?? ''}
          onChange={(event) => {
            setParam('type', event.target.value === '' ? null : event.target.value);
          }}
          style={selectStyle}
        >
          <option value="">{STR.access.queue.typeAny}</option>
          {ACCESS_QUEUE_TYPES.map((type) => (
            <option key={type} value={type}>
              {STR.access.queue.type[type]}
            </option>
          ))}
          {hrOnly ? null : (
            <option value="deactivations">{STR.access.queue.deactivations.title}</option>
          )}
          <option value={WAITING_ON_IT_LANE}>{STR.itEmails.lane}</option>
        </select>
        {itOnly ? null : (
          <>
            <select
              className="field"
              aria-label={STR.access.queue.ageLabel}
              value={age}
              onChange={(event) => {
                const next = event.target.value as AgeFilter;
                setAgeChoice(next);
                if (typeParam === 'overdue' && next !== 'overdue') setParam('type', null);
              }}
              style={selectStyle}
            >
              <option value="all">{STR.access.queue.ageAny}</option>
              <option value="overdue">{STR.access.queue.ageOverdue}</option>
              <option value="ontime">{STR.access.queue.ageOnTime}</option>
            </select>
            {levelList.map((level) => {
              const options = liveNodes.filter((node) => node.level.id === level.id);
              if (options.length === 0) return null;
              return (
                <select
                  key={level.id}
                  className="field"
                  aria-label={STR.access.queue.levelLabel(level.name)}
                  value={levelPicks[level.id] ?? ''}
                  onChange={(event) => {
                    const value = event.target.value;
                    setLevelPicks((current) => ({ ...current, [level.id]: value }));
                  }}
                  style={selectStyle}
                >
                  <option value="">{STR.access.queue.levelAny(level.name)}</option>
                  {options.map((node) => (
                    <option key={node.id} value={node.id}>
                      {node.name}
                    </option>
                  ))}
                </select>
              );
            })}
            <select
              className="field"
              aria-label={STR.access.queue.personLabel}
              value={userFilter ?? ''}
              onChange={(event) => {
                setParam('user', event.target.value === '' ? null : event.target.value);
              }}
              style={selectStyle}
            >
              <option value="">{STR.access.queue.personAny}</option>
              {people.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name}
                </option>
              ))}
            </select>
          </>
        )}
        {filtersActive ? (
          <button
            type="button"
            className="tbtn"
            style={quietButtonStyle}
            onClick={() => {
              setSearch('');
              setAgeChoice('all');
              setLevelPicks({});
              setParams(new URLSearchParams(), { replace: true });
            }}
          >
            {STR.access.queue.clearFilters}
          </button>
        ) : null}
      </div>

      {/* The bulk bar never appears or disappears, so the lanes below it never jump. */}
      {itOnly ? null : (
        <Card style={{ overflow: 'visible', position: 'sticky', top: 0, zIndex: 6 }}>
          <div
            style={{
              display: 'flex',
              gap: 6,
              alignItems: 'center',
              flexWrap: 'wrap',
              padding: '8px 12px',
              background: 'var(--surface)',
              borderRadius: 12,
            }}
          >
            <span
              style={{
                fontSize: 12.5,
                fontWeight: 650,
                minWidth: 84,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {STR.access.queue.selectedCount(selectedItems.length)}
            </span>
            {selectedItems.length > 0 ? (
              <>
                <button
                  type="button"
                  className="prim"
                  style={rowActionStyle}
                  disabled={busy || nRequests === 0}
                  onClick={() => {
                    startApprove(selectedItems);
                  }}
                >
                  {STR.access.queue.bulkApprove(nRequests)}
                </button>
                <button
                  type="button"
                  className="ghost"
                  style={rowActionStyle}
                  disabled={busy || nRequests === 0}
                  onClick={() => {
                    startDeny(selectedItems);
                  }}
                >
                  {STR.access.queue.bulkDeny(nRequests)}
                </button>
                <button
                  type="button"
                  className="ghost"
                  style={rowActionStyle}
                  disabled={busy || selectedForIt.length === 0}
                  onClick={() => {
                    setItFlow({
                      kind: 'pick',
                      initial: new Set(selectedForIt.map((item) => item.id)),
                    });
                  }}
                >
                  {STR.itEmails.bulkDraft(selectedForIt.length)}
                </button>
                <button
                  type="button"
                  className="ghost"
                  style={rowActionStyle}
                  disabled={busy || nGranted === 0}
                  onClick={() => {
                    mark('mark_granted', selectedItems);
                  }}
                >
                  {STR.access.queue.bulkGranted(nGranted)}
                </button>
                <button
                  type="button"
                  className="ghost"
                  style={rowActionStyle}
                  disabled={busy || nRemoved === 0}
                  onClick={() => {
                    mark('mark_removed', selectedItems);
                  }}
                >
                  {STR.access.queue.bulkRemoved(nRemoved)}
                </button>
                <button
                  type="button"
                  className="tbtn"
                  style={rowActionStyle}
                  onClick={() => {
                    setSelected(new Set());
                  }}
                >
                  {STR.access.queue.clearSelection}
                </button>
              </>
            ) : (
              <>
                <span className="tx-subtle" style={{ fontSize: 12 }}>
                  {STR.access.queue.selectAllShown}
                </span>
                <button
                  type="button"
                  className="tbtn"
                  style={rowActionStyle}
                  onClick={() => {
                    selectBy('overdue');
                  }}
                >
                  {STR.access.queue.quickOverdue}
                </button>
                {lanesShown.map((type) => (
                  <button
                    key={type}
                    type="button"
                    className="tbtn"
                    style={rowActionStyle}
                    onClick={() => {
                      selectBy(type);
                    }}
                  >
                    {STR.access.queue.type[type]}
                  </button>
                ))}
              </>
            )}
            <span className="tx-subtle" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
              {STR.access.queue.bulkFits}
            </span>
          </div>
        </Card>
      )}

      {/* `?type=it` shows the Waiting on IT lane alone, below. */}
      {itOnly ? null : all.length === 0 && allDeactivations.length === 0 ? (
        <>
          <Card>
            <EmptyRow>{STR.access.queue.empty}</EmptyRow>
          </Card>
          {showOther ? <OtherRequestsLane focusId={focusOther} /> : null}
        </>
      ) : shown.length === 0 && deactivations.length === 0 && done.length === 0 ? (
        <>
          <Card>
            <EmptyRow>{STR.access.queue.emptyFiltered}</EmptyRow>
          </Card>
          {showOther ? <OtherRequestsLane focusId={focusOther} /> : null}
        </>
      ) : (
        <>
          {lanesShown.map((type) => (
            <Fragment key={type}>
              {lane(type)}
              {/* Other requests are requests: their lane sits right under the Requests lane. */}
              {type === 'request' && showOther ? <OtherRequestsLane focusId={focusOther} /> : null}
            </Fragment>
          ))}
          {hrOnly ? null : deactivationLane(deactivations)}
        </>
      )}

      {/* Waiting on IT (spec row 15): after the lanes its work came from. */}
      {(typeFilter === null || itOnly) && itEmails.data !== undefined ? (
        <WaitingOnItLane
          emails={itEmails.data.data}
          overdueAfterDays={itEmails.data.meta.overdueAfterDays}
          onOpen={(email) => {
            setItFlow({ kind: 'email', email });
          }}
        />
      ) : itOnly && itEmails.isError ? (
        <Callout
          tone="danger"
          role="alert"
          style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
        >
          <span style={{ flex: 1, minWidth: 180 }}>{STR.access.queue.loadFailed}</span>
          <button
            type="button"
            className="tbtn"
            style={quietButtonStyle}
            onClick={() => {
              void itEmails.refetch();
            }}
          >
            {STR.common.tryAgain}
          </button>
        </Callout>
      ) : itOnly ? (
        <Skeleton style={{ height: 140, borderRadius: 12 }} />
      ) : null}

      {done.length > 0 ? (
        <div>
          <button
            type="button"
            className="tbtn"
            style={quietButtonStyle}
            onClick={() => {
              setDone([]);
            }}
          >
            {STR.access.queue.clearDone(done.length)}
          </button>
        </div>
      ) : null}

      {itFlow === null ? null : (
        <ItEmailFlow
          start={itFlow}
          pickable={forIt}
          queueItems={queue.items}
          onClose={() => {
            // The ticks that opened the picker are spent: whatever was drafted is in a live
            // email now and cannot go in a second one.
            if (itFlow.kind === 'pick') {
              const spent = new Set(
                forIt.filter((item) => itFlow.initial.has(item.id)).map(itemKey),
              );
              setSelected((current) => new Set([...current].filter((key) => !spent.has(key))));
            }
            setItFlow(null);
          }}
        />
      )}
      {dialog?.kind === 'approve' ? (
        <ApproveDialog
          items={dialog.items}
          pending={busy}
          error={dialogError}
          onCancel={closeDialog}
          onConfirm={(modes) => {
            mutation.mutate({ kind: 'approve', items: dialog.items, modes });
          }}
        />
      ) : null}
      {dialog?.kind === 'deny' ? (
        <DenyDialog
          items={dialog.items}
          pending={busy}
          error={dialogError}
          onCancel={closeDialog}
          onConfirm={(note) => {
            mutation.mutate({ kind: 'deny', items: dialog.items, note });
          }}
        />
      ) : null}
    </div>
  );
}
